import { useMutation } from '@tanstack/react-query'
import { type SubmitEvent, useState } from 'react'

import { Button } from '@/components/button'
import { FormError } from '@/components/form-error'
import { emptyBranchDraft, toBranchRequest } from '@/features/restaurants/branch-draft'
import { BranchFields } from '@/features/restaurants/branch-fields'
import { useSlugVerdict } from '@/features/restaurants/use-slug-verdict'
import { ApiError, request } from '@/lib/api'
import { useT } from '@/lib/i18n/translate'

/**
 * The add-branch form, one POST to `path`: the platform detail page (E2) and the back-office
 * Branches page (K2) share it. Mount it fresh per opening; the draft lives here.
 */
export function AddBranchForm({
  path,
  siblingSlugs,
  onAdded,
  onCancel,
}: {
  path: string
  /** The offline branch-slug check (URL scheme): the restaurant's own branches. */
  siblingSlugs: readonly string[]
  /** Runs once the branch is in; the form stays pending until it settles. */
  onAdded: (branch: { name: string }) => Promise<unknown> | void
  onCancel: () => void
}) {
  const t = useT()
  const [draft, setDraft] = useState(emptyBranchDraft)
  const [submitted, setSubmitted] = useState(false)
  const slugVerdict = useSlugVerdict(draft.slug, { taken: siblingSlugs })

  const add = useMutation({
    mutationFn: () => request<{ name: string }>(path, { method: 'POST', body: toBranchRequest(draft) }),
    onSuccess: (branch) => onAdded(branch),
  })

  function submit(event: SubmitEvent) {
    event.preventDefault()
    setSubmitted(true)
    if (!slugVerdict.ok) return
    add.mutate()
  }

  const slugError = add.error instanceof ApiError && add.error.code.startsWith('SLUG_') ? add.error : undefined

  return (
    <form onSubmit={submit} className="border-border flex flex-col gap-4 border-t pt-4">
      <BranchFields
        value={draft}
        onChange={(next) => {
          setDraft(next)
          if (slugError) add.reset()
        }}
        slugVerdict={slugVerdict}
        slugError={slugError}
        submitted={submitted}
      />
      <FormError error={slugError ? undefined : add.error} />
      <div className="flex gap-2">
        <Button type="submit" loading={add.isPending}>
          {t('restaurants.addBranch')}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  )
}
